'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { useSubscribeToCartRefresh } from '@/lib/broadcast/cartSync';
import { ensurePartyOrderFulfillment } from '@/lib/functions/ensurePartyOrderFulfillment';
import { ensurePartyOrderSubstitutions } from '@/lib/functions/ensurePartyOrderSubstitutions';
import { getOrderByToken } from '@/lib/functions/getOrderByToken';
import { orderHasShippingEstimate } from '@/lib/functions/orderHasShippingEstimate';
import { useOrder } from '@/lib/context/orderContext';

import type { CartSyncMessage } from '@/lib/types/boadcast/message';
import type { OrderPayload } from '@/lib/types/orderPayload';

const MAX_SYNC_RETRIES = 3;

export { orderHasShippingEstimate } from '@/lib/functions/orderHasShippingEstimate';

export function usePartyCheckoutOrder() {
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  const [orderData, setOrderData] = useState<OrderPayload | null>(null);
  const [loadingCart, setLoadingCart] = useState(false);
  const [mounted, setMounted] = useState(false);
  const syncInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const syncCart = useCallback(async (showLoader: boolean, retries = 0) => {
    if (showLoader) setLoadingCart(true);

    const fetched = await getOrderByToken(orderState, orderDispatch);

    const fulfillmentUpdated = await ensurePartyOrderFulfillment(
      orderState,
      orderDispatch,
      fetched,
    );

    if (fulfillmentUpdated && retries < MAX_SYNC_RETRIES) {
      return syncCart(showLoader, retries + 1);
    }

    const substitutionsUpdated = orderState.token
      ? await ensurePartyOrderSubstitutions(orderState.token, fetched)
      : false;

    if (substitutionsUpdated && retries < MAX_SYNC_RETRIES) {
      return syncCart(showLoader, retries + 1);
    }

    setOrderData(fetched);
    if (showLoader) setLoadingCart(false);
  }, [orderState, orderDispatch]);

  const syncCartOnce = useCallback((showLoader: boolean) => {
    if (syncInFlightRef.current) return syncInFlightRef.current;

    const run = syncCart(showLoader)
      .catch(() => {
        // errors already handled inside getOrderByToken
      })
      .finally(() => {
        syncInFlightRef.current = null;
      });

    syncInFlightRef.current = run;
    return run;
  }, [syncCart]);

  const handleCartRefresh = useCallback((message: CartSyncMessage) => {
    if (message.type !== 'cart_refreshed') return;
    if (!orderState?.token) return;
    syncCartOnce(false);
  }, [orderState?.token, syncCartOnce]);

  useSubscribeToCartRefresh(handleCartRefresh, 1000);

  useEffect(() => {
    if (orderState?.token) {
      syncCartOnce(true);
    } else {
      setOrderData(null);
      setLoadingCart(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState?.token]);

  return {
    mounted,
    orderData,
    setOrderData,
    loadingCart,
    syncCartOnce,
    orderState,
  };
}
